import { Button } from "@/components/Button";
import { SelectInput } from "@/components/SelectInput";
import { TextInput } from "@/components/TextInput";
import { useProvider } from "@/pages/providers/context/provider-context";
import { useApp } from "@/services/app-context";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HowDidYouHearAboutUs } from "../../patient.types";
import FilledCheckboxIcon from "../../../../assets/icons/checkbox-filled.svg?react";
import CloseIcon from "../../../../assets/icons/x.svg?react";
import { RegistrationFormData } from "../context/registration-context";
import { ProviderInfoSection } from "../components/ProviderInfoSection";


interface GetStartedFormProps {
  onContinue: () => void;
  isFromLogin?: boolean;
  partialSignUpInfo?: any;
  error?: string;
  formData: RegistrationFormData;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
}

export const GetStartedForm: React.FC<GetStartedFormProps> = ({
  onContinue,
  partialSignUpInfo,
  error: externalError,
  formData,
  updateFormData
}) => {
  const { providers } = useProvider() as any;
  const { selectedProvider } = useApp() as any;
  const [error, setError] = useState<string>("");
  const [hideProviderSelection, setHideProviderSelection] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Use external error if provided, otherwise use internal error
  const displayError = externalError || error;

  useEffect(() => {
    if (partialSignUpInfo) {
      updateFormData(partialSignUpInfo);
    }
  }, [partialSignUpInfo, updateFormData]);

  const textReasonRequiredHeardAboutUs = formData.howDidYouHearAboutUs && [
    HowDidYouHearAboutUs.ReferralFromAnotherCareProvider,
    HowDidYouHearAboutUs.HeadstartCareProvider,
    HowDidYouHearAboutUs.SchoolOrDaycare,
    HowDidYouHearAboutUs.ParentSupportGroupOrResourceCenter,
    HowDidYouHearAboutUs.Other
  ].includes(formData.howDidYouHearAboutUs);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDirtyFields(prev => new Set([...prev, name]));
    updateFormData({ [name]: value });
  };

  const handleCheckboxChange = (name: string, value: boolean) => {
    setDirtyFields(prev => new Set([...prev, name]));
    updateFormData({ [name]: value });
  };

  // Get all validation errors (frontend + backend)
  const getValidationErrors = (): string[] => {
    const validationErrors: string[] = [];
    
    // Frontend validation errors (only for dirty fields)
    if (dirtyFields.has('guardianFirstName') && !formData.guardianFirstName) {
      validationErrors.push("Guardian first name is required");
    }
    if (dirtyFields.has('guardianLastName') && !formData.guardianLastName) {
      validationErrors.push("Guardian last name is required");
    }
    if (dirtyFields.has('email') && !formData.email) {
      validationErrors.push("Email is required");
    }
    if (dirtyFields.has('mobile') && !formData.mobile) {
      validationErrors.push("Mobile number is required");
    }
    if (dirtyFields.has('howDidYouHearAboutUs') && !formData.howDidYouHearAboutUs) {
      validationErrors.push("Please select how you heard about us");
    }
    if (dirtyFields.has('howDidYouHearAboutUsText') && textReasonRequiredHeardAboutUs && !formData.howDidYouHearAboutUsText) {
      validationErrors.push(`${formData.howDidYouHearAboutUs} - Please explain`);
    }
    if (dirtyFields.has('privacyConsent') && !formData.privacyConsent) {
      validationErrors.push("You must consent to the privacy policy");
    }
    
    // Backend errors (always show)
    if (displayError) {
      validationErrors.push(displayError);
    }
    
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setError("");
      onContinue();
    } catch (error: any) {
      setError(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleClose = () => {
    navigate('/login');
  };

  const handleBrowseProviders = () => {
    navigate('/providers');
  };

  const handleSelectLater = () => {
    setHideProviderSelection(true);
  };

  const disabled = useMemo(() => {
    return !formData.guardianFirstName ||
           !formData.guardianLastName ||
           !formData.email ||
           !formData.mobile ||
           !formData.howDidYouHearAboutUs ||
           (textReasonRequiredHeardAboutUs && !formData.howDidYouHearAboutUsText) ||
           !formData.privacyConsent;
  }, [formData, textReasonRequiredHeardAboutUs]);

  return (
    <>
      <div className="fixed z-[9] top-0 left-0 right-0 bottom-0 bg-black/40 h-[100vh] flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
        <div className="bg-white p-4 sm:p-8 rounded-[16px] max-w-[700px] w-full h-[calc(100vh-1rem)] sm:h-auto sm:max-h-[90vh] overflow-y-auto my-auto">
          <div className="flex justify-end items-center mb-4">
            <button 
              onClick={handleClose}
              className="sm:translate-x-[10px] sm:-translate-y-[10px] -translate-y-1"
            >
              <CloseIcon className="w-5 h-5 opacity-80" />
            </button>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-center text-4xl text-black font-bold mt-[-20px] mb-3">
              Get Started
            </h1>
          </div>

          {!hideProviderSelection && (
            <div className="max-w-[400px] mx-auto border-2 border-primary rounded-lg mb-6">
              <div className="col-span-1 flex justify-center">
                <div className="flex items-center mt-2 mb-[-2px] text-sm text-left font-semibold">
                  {selectedProvider?.firstName ? 'Your Provider' : 'You have not selected a provider'}
                </div>
              </div>
              <ProviderInfoSection 
                provider={selectedProvider} 
                onBrowseProviders={handleBrowseProviders}
                onSelectLater={handleSelectLater}
                onSelectDifferentProvider={handleBrowseProviders}
              />
            </div>
          )}

          <form
            className="mt-4 flex flex-col gap-[22px]"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="flex-1 w-full flex flex-col gap-1.5">
                <label className="text-sm text-black/80">
                  Parent / Guardian First Name
                </label>
                <TextInput
                  name="guardianFirstName"
                  value={formData.guardianFirstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  maxLength={128}
                />
              </div>
              <div className="flex-1 w-full flex flex-col gap-1.5">
                <label className="text-sm text-black/80">
                  Parent / Guardian Last Name
                </label>
                <TextInput
                  name="guardianLastName"
                  value={formData.guardianLastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  maxLength={128}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="flex-1 w-full flex flex-col gap-1.5">
                <label className="text-sm text-black/80">Email</label>
                <TextInput
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  name="email"
                  maxLength={128}
                />
              </div>
              <div className="flex-1 w-full flex flex-col gap-1.5">
                <label className="text-sm text-black/80">Phone Number</label>
                <TextInput
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.mobile}
                  onChange={handleChange}
                  name="mobile"
                  maxLength={10}
                />
              </div>
            </div>

            {!(partialSignUpInfo?.howDidYouHearAboutUs && (!textReasonRequiredHeardAboutUs || partialSignUpInfo?.howDidYouHearAboutUsText)) && (
              <>
                <div>
                  <label className="text-sm text-black/80 mb-[-15px]">
                    How did you hear about Headstart?
                  </label>
                  <SelectInput
                    data={Object.values(HowDidYouHearAboutUs).map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    name="howDidYouHearAboutUs"
                    value={formData.howDidYouHearAboutUs}
                    disabledOptionText={"Select how you heard about Headstart"}
                    onChange={(e) => {
                      updateFormData({ howDidYouHearAboutUs: e.target.value as HowDidYouHearAboutUs });
                    }}
                    className={`mt-0 ${!formData.howDidYouHearAboutUs && "text-slate-400"}`}
                  />
                </div>

                {textReasonRequiredHeardAboutUs && (
                  <div className="flex-1 w-full flex flex-col gap-1.5">
                    <label className="text-sm text-black/80">
                      {formData.howDidYouHearAboutUs === HowDidYouHearAboutUs.HeadstartCareProvider
                        && "What is the name of the Headstart care provider that referred you?"}
                      {formData.howDidYouHearAboutUs === HowDidYouHearAboutUs.ReferralFromAnotherCareProvider
                        && "What is the name of the institution or provider that referred you? (e.g. Happy Family Pediatrics, or Dr. John Smith)"}
                      {formData.howDidYouHearAboutUs === HowDidYouHearAboutUs.SchoolOrDaycare
                        && "What is the name of the facility that referred you? (e.g. Sunny Horizons Daycare)"}
                      {formData.howDidYouHearAboutUs === HowDidYouHearAboutUs.ParentSupportGroupOrResourceCenter
                        && "What is the name of the group or resource center?"}
                      {formData.howDidYouHearAboutUs === HowDidYouHearAboutUs.Other
                        && "Other (please specify):"}
                    </label>
                    {formData.howDidYouHearAboutUs === HowDidYouHearAboutUs.HeadstartCareProvider ? (
                      <SelectInput
                        data={providers?.map((provider) => ({
                          label: provider.firstName + " " + provider.lastName,
                          value: provider.firstName + " " + provider.lastName,
                        })) || []}
                        name="howDidYouHearAboutUsText"
                        value={formData.howDidYouHearAboutUsText}
                        disabledOptionText={"Name of referring Headstart care provider?"}
                        onChange={handleChange}
                        className={`mt-0 ${!formData.howDidYouHearAboutUsText && "text-slate-400"}`}
                      />
                    ) : (
                      <TextInput
                        type="text"
                        placeholder=""
                        value={formData.howDidYouHearAboutUsText}
                        onChange={handleChange}
                        name="howDidYouHearAboutUsText"
                        maxLength={128}
                      />
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex items-center gap-1.5 mt-3">
              <div className="flex">
                <input
                  type="checkbox"
                  id="privacyConsent"
                  name="privacyConsent"
                  checked={formData.privacyConsent}
                  onChange={(e) =>
                    handleCheckboxChange("privacyConsent", e.target.checked)
                  }
                  className={`w-[22px] h-[22px] cursor-pointer ${
                    formData.privacyConsent ? "opacity-[0] !h-0 !w-0" : ""
                  }`}
                />
                {formData.privacyConsent ? (
                  <button
                    onClick={() => handleCheckboxChange("privacyConsent", false)}
                  >
                    <FilledCheckboxIcon className="w-[22px] h-[22px]" />
                  </button>
                ) : null}
              </div>
              <label
                htmlFor="privacyConsent"
                className="text-base text-[#1D1E1B]"
              >
                I consent to Headstart's{" "}
                <a
                  href="https://dev.headstart.health/privacy-policy"
                  className="text-primary font-medium hover:text-black"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Single error summary box */}
            {getValidationErrors().length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-red-800 text-sm font-medium mb-2">
                  Please fix the following errors:
                </div>
                <ul className="text-red-700 text-sm space-y-1">
                  {getValidationErrors().map((error, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3">
              <div className="flex gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  className="!rounded-full flex-1"
                  disabled={disabled}
                >
                  Continue
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}; 